import { type FC, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LabelIcon from '@mui/icons-material/Label';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import { getLabelColor } from '../../pastelPalette';
import type { Label, LabelGroup } from '../../types';

export interface LabelRowProps {
  label: Label;
  /** Groups available as "Move to" targets. */
  groups: LabelGroup[];
  /** Render with the group child-indent (used inside a group's guide rail). */
  indented?: boolean;
  onRename: (name: string) => void;
  onMove: (groupId: string | null) => void;
  onDelete: () => void;
}

/**
 * A single label row (GDN-2019). Shows the pastel `LabelIcon`, an inline-editable
 * name, and a `•••` overflow menu (rename / move to a group / delete). Reused for
 * both grouped (indented) and standalone labels.
 */
export const LabelRow: FC<LabelRowProps> = ({
  label,
  groups,
  indented = false,
  onRename,
  onMove,
  onDelete,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label.name);

  const closeMenu = () => setAnchorEl(null);

  const startEdit = () => {
    setDraft(label.name);
    setEditing(true);
    closeMenu();
  };

  const commitEdit = () => {
    const next = draft.trim();
    if (next && next !== label.name) onRename(next);
    setEditing(false);
  };

  const move = (groupId: string | null) => {
    if (groupId !== label.label_group_id) onMove(groupId);
    closeMenu();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.5,
        pl: indented ? 1.5 : 0,
        '&:hover .label-row-actions': { opacity: 1 },
      }}
    >
      <LabelIcon sx={{ fontSize: 16, color: getLabelColor(label.color) }} />

      {editing ? (
        <TextField
          autoFocus
          size="small"
          variant="standard"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={e => {
            if (e.key === 'Enter') commitEdit();
            if (e.key === 'Escape') setEditing(false);
          }}
          sx={{ flex: 1 }}
        />
      ) : (
        <Typography
          variant="body2"
          sx={{ flex: 1 }}
          onDoubleClick={startEdit}
        >
          {label.name}
        </Typography>
      )}

      <IconButton
        size="small"
        className="label-row-actions"
        aria-label={`Actions for ${label.name}`}
        onClick={e => setAnchorEl(e.currentTarget)}
        sx={{ opacity: 0, transition: 'opacity 120ms' }}
      >
        <MoreHorizIcon fontSize="small" />
      </IconButton>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        <MenuItem onClick={startEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit label name</ListItemText>
        </MenuItem>

        <Divider />
        <MenuItem disabled sx={{ opacity: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Move to
          </Typography>
        </MenuItem>
        <MenuItem
          selected={label.label_group_id === null}
          onClick={() => move(null)}
        >
          <ListItemText inset>Standalone</ListItemText>
        </MenuItem>
        {groups.map(group => (
          <MenuItem
            key={group.label_group_id}
            selected={label.label_group_id === group.label_group_id}
            onClick={() => move(group.label_group_id)}
          >
            <ListItemText inset>{group.name}</ListItemText>
          </MenuItem>
        ))}

        <Divider />
        <MenuItem
          onClick={() => {
            onDelete();
            closeMenu();
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};
